/** Arquivo da Loja: pastas, arquivos, vínculos com irmãos e busca. */

import { api } from './client';
import type {
  ArquivoDaLoja,
  ConteudoDaPasta,
  FiltrosDeBusca,
  IrmaoVinculado,
  MeuArquivo,
  PastaDetalhe,
  PastaNaArvore,
  RespostaDeBusca,
} from '../types';

const buscar = async <T,>(url: string, params?: Record<string, unknown>): Promise<T> =>
  (await api.get<T>(url, { params })).data;

export interface EnvioDeArquivos {
  pastaId: number;
  arquivos: File[];
  descricao?: string;
  etiquetas?: string;
  irmaosIds?: number[];
  reservado?: boolean;
}

export const ApiArquivo = {
  arvore: () => buscar<PastaNaArvore[]>('/arquivo/arvore'),
  conteudo: (pastaId: number | null) =>
    buscar<ConteudoDaPasta>('/arquivo/conteudo', pastaId ? { pasta_id: pastaId } : undefined),
  irmaos: () => buscar<IrmaoVinculado[]>('/arquivo/irmaos'),
  buscar: (filtros: FiltrosDeBusca) => buscar<RespostaDeBusca>('/arquivo/busca', { ...filtros, limite: 80 }),

  criarPasta: async (dados: {
    nome: string;
    descricao?: string;
    pasta_pai_id?: number | null;
    irmaos_ids?: number[];
    publica?: boolean;
  }): Promise<PastaDetalhe> => (await api.post<PastaDetalhe>('/arquivo/pastas', dados)).data,
  editarPasta: async (
    id: number,
    dados: {
      nome?: string;
      descricao?: string;
      pasta_pai_id?: number;
      mover_para_raiz?: boolean;
      irmaos_ids?: number[];
      publica?: boolean;
    },
  ): Promise<PastaDetalhe> => (await api.patch<PastaDetalhe>(`/arquivo/pastas/${id}`, dados)).data,
  excluirPasta: async (id: number): Promise<void> => {
    await api.delete(`/arquivo/pastas/${id}`);
  },

  enviar: async (envio: EnvioDeArquivos): Promise<ArquivoDaLoja[]> => {
    const corpo = new FormData();
    for (const a of envio.arquivos) corpo.append('arquivos', a);
    if (envio.descricao) corpo.append('descricao', envio.descricao);
    if (envio.etiquetas) corpo.append('etiquetas', envio.etiquetas);
    for (const id of envio.irmaosIds ?? []) corpo.append('irmaos_ids', String(id));
    if (envio.reservado) corpo.append('reservado', 'true');
    // Lote de PDFs escaneados passa fácil dos 30 s padrão do cliente.
    return (
      await api.post<ArquivoDaLoja[]>(`/arquivo/pastas/${envio.pastaId}/arquivos`, corpo, {
        timeout: 600_000,
      })
    ).data;
  },
  arquivo: (id: number) => buscar<ArquivoDaLoja>(`/arquivo/arquivos/${id}`),
  editarArquivo: async (
    id: number,
    dados: {
      nome?: string;
      descricao?: string;
      etiquetas?: string;
      pasta_id?: number;
      irmaos_ids?: number[];
      reservado?: boolean;
    },
  ): Promise<ArquivoDaLoja> => (await api.patch<ArquivoDaLoja>(`/arquivo/arquivos/${id}`, dados)).data,
  excluirArquivo: async (id: number): Promise<void> => {
    await api.delete(`/arquivo/arquivos/${id}`);
  },
  /** Os bytes como blob — o bucket nunca é exposto ao navegador. */
  blob: async (endpoint: string): Promise<Blob> =>
    (await api.get<Blob>(endpoint, { responseType: 'blob', timeout: 600_000 })).data,

  meu: () => buscar<MeuArquivo>('/arquivo/meu'),
  /** Uma pasta pública, como o irmão a vê (sem reservados, sem vínculos). */
  minhaPasta: (pastaId: number) => buscar<ConteudoDaPasta>(`/arquivo/meu/pastas/${pastaId}`),
};

export const endpointDoArquivo = (id: number, baixar = false) =>
  `/arquivo/arquivos/${id}/conteudo${baixar ? '?baixar=true' : ''}`;
export const endpointDoMeuArquivo = (id: number, baixar = false) =>
  `/arquivo/meu/arquivos/${id}/conteudo${baixar ? '?baixar=true' : ''}`;
